import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from "react";
import { copyDiagnostics, DIAGNOSTICS_COPIED_NOTICE } from "@/lib/diagnostics";
import { useNoticeToast } from "@/lib/noticeToastStore";
import { NavigationIcon as Icon } from "@/components/NavigationIcon";
import { OrgIdentity } from "@/components/OrgIdentity";
import { useDismissOnOutside } from "@/lib/dismissOnOutside";

/**
 * The account menu behind the sidebar's organization selector.
 *
 * Everything that used to be scattered along the sidebar footer — the org
 * switcher, the settings gear, the platform-admin link — plus the sign-out that
 * was only reachable from a settings page lives here, in the one control that
 * names the account you are signed in as. It is built on the app's single menu
 * system (`--surface-1` card, 28px rows, 4px padding) rather than a new panel,
 * and on plain buttons rather than a `<select>`, because a menu that looks like
 * a different component makes the app feel like two apps.
 *
 * "Switch workspace" is the one nested row. On desktop it opens a real flyout
 * beside its parent, flipping to the left edge when the viewport has no room;
 * on a phone the same list is pushed into the card behind a "Back" row, because
 * a flyout hanging off a 300px drawer has nowhere to go.
 *
 * Keyboard follows the ARIA menu pattern rather than the tab order: the card
 * itself holds focus, every row is `tabIndex={-1}`, and the cursor is published
 * as `aria-activedescendant` so a screen reader announces the row the arrow
 * keys are on. The cursor is keyed by row *id*, not by position, so "Platform
 * admin" appearing once `me` resolves cannot shift it out from under you.
 */

export type AccountMenuOrg = { id: string; name: string; image?: string | null };

/** The flyout's width, in one place: the element carries it as an inline style,
 * so the measurement below and the paint can never disagree. */
export const FLYOUT_WIDTH = 240;
const VIEWPORT_MARGIN = 8;
/** Long enough that dragging the pointer diagonally past the row does nothing. */
const HOVER_OPEN_DELAY_MS = 120;
/** The pushed-page "Back" row, which shares the workspace list's cursor. The
 * leading space cannot collide with an organization id. */
const BACK_ROW_ID = " back";

/** Which side the workspace flyout opens on. Pure, so it can be tested. */
export function flyoutSide(
  anchorRight: number,
  viewportWidth: number,
  width = FLYOUT_WIDTH,
  margin = VIEWPORT_MARGIN,
): "right" | "left" {
  return anchorRight + width + margin <= viewportWidth ? "right" : "left";
}

/** How far up the flyout has to shift to stay inside the viewport. */
export function flyoutShift(anchorTop: number, height: number, viewportHeight: number, margin = VIEWPORT_MARGIN): number {
  const overflow = anchorTop + height + margin - viewportHeight;
  return overflow > 0 ? -Math.min(overflow, Math.max(0, anchorTop - margin)) : 0;
}

type Row =
  | { kind: "separator" }
  | { kind: "action"; id: string; label: string; danger?: boolean; onSelect: () => void }
  | { kind: "submenu"; id: string; label: string };

/** Only one account menu is ever mounted, so a static prefix is unique enough
 * for `aria-activedescendant` and stays readable in the accessibility tree. */
function domId(id: string): string {
  return `account-menu-item-${encodeURIComponent(id)}`;
}

export function AccountMenu({
  open,
  onClose,
  triggerRef,
  orgId,
  orgs,
  userEmail,
  isSuperAdmin = false,
  onSettings,
  onMembers,
  onPlatformAdmin,
  onSelectOrg,
  onSignOut,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  orgId: string;
  orgs: AccountMenuOrg[];
  userEmail?: string;
  isSuperAdmin?: boolean;
  onSettings: () => void;
  onMembers: () => void;
  onPlatformAdmin: () => void;
  onSelectOrg: (orgId: string) => void;
  onSignOut: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuAnchorRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [activeId, setActiveId] = useState("settings");
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [submenuId, setSubmenuId] = useState("");
  const [placement, setPlacement] = useState<{ side: "right" | "left"; shift: number }>({ side: "right", shift: 0 });
  const [narrow, setNarrow] = useState(false);

  // A phone opens the workspace list *inside* the card; a desktop opens it
  // beside. The query is read live so a resize mid-menu cannot strand a flyout
  // off the edge of a drawer.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(max-width: 768px)");
    const apply = () => setNarrow(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  const rows: Row[] = [
    { kind: "action", id: "settings", label: "Settings", onSelect: onSettings },
    { kind: "action", id: "members", label: "Invite and manage members", onSelect: onMembers },
    ...(isSuperAdmin ? [{ kind: "action", id: "admin", label: "Platform admin", onSelect: onPlatformAdmin } as Row] : []),
    { kind: "separator" },
    { kind: "submenu", id: "switch", label: "Switch workspace" },
    { kind: "separator" },
    // What went wrong in this page, for a bug report: connection history,
    // relay state and recent errors (CONTRACT "Diagnostics").
    { kind: "action", id: "diagnostics", label: "Copy diagnostics", onSelect: () => {
      void copyDiagnostics().then((copied) => useNoticeToast.getState().show(copied
        ? DIAGNOSTICS_COPIED_NOTICE
        : "Diagnostics could not be copied. Allow clipboard access and try again."));
    } },
    { kind: "action", id: "signout", label: "Log out", danger: true, onSelect: onSignOut },
  ];
  const selectableIds = rows.flatMap((row) => (row.kind === "separator" ? [] : [row.id]));
  // The pushed page puts "Back" at the head of the same cursor cycle; the
  // desktop flyout has no such row, so the cycle is just the workspaces.
  const submenuIds = [...(narrow ? [BACK_ROW_ID] : []), ...orgs.map((candidate) => candidate.id)];

  useEffect(() => {
    if (!open) {
      // A menu closed while the pointer sat on "Switch workspace" must not wake
      // up 120ms later and flip a submenu open behind its own trigger.
      clearTimeout(hoverTimer.current);
      return;
    }
    setActiveId("settings");
    setSubmenuOpen(false);
    menuRef.current?.focus();
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  // Every dismissal goes through here, so the hover timer is always disarmed
  // and the submenu never outlives its parent.
  const closeAll = useCallback(() => {
    clearTimeout(hoverTimer.current);
    setSubmenuOpen(false);
    onClose();
  }, [onClose]);

  // An outside pointer-down, a scroll *underneath the trigger*, a resize, or
  // the window losing focus — the same four gestures that dismiss the
  // right-click menu. Anchored to the trigger so a transcript streaming in the
  // pane beside the sidebar does not count as the menu's ground moving.
  useDismissOnOutside(open, [menuRef, triggerRef], closeAll, { anchor: triggerRef });

  // Measure once the flyout exists: near the right edge of a narrow window it
  // flips to the other side of the card, and a long org list rides up rather
  // than running off the bottom.
  useLayoutEffect(() => {
    if (!submenuOpen || narrow) return;
    const anchor = submenuAnchorRef.current;
    const flyout = flyoutRef.current;
    if (!anchor || !flyout) return;
    const anchorRect = anchor.getBoundingClientRect();
    const { height, width } = flyout.getBoundingClientRect();
    setPlacement({
      side: flyoutSide(anchorRect.right, window.innerWidth, width),
      shift: flyoutShift(anchorRect.top - 4, height, window.innerHeight),
    });
  }, [narrow, orgs.length, submenuOpen]);

  if (!open) return null;

  const dismissToTrigger = () => {
    closeAll();
    triggerRef.current?.focus();
  };

  const openSubmenu = () => {
    clearTimeout(hoverTimer.current);
    const current = orgs.some((candidate) => candidate.id === orgId) ? orgId : undefined;
    setSubmenuId(current ?? (narrow ? BACK_ROW_ID : orgs[0]?.id ?? BACK_ROW_ID));
    setSubmenuOpen(true);
  };

  const closeSubmenu = () => {
    clearTimeout(hoverTimer.current);
    setSubmenuOpen(false);
    setActiveId("switch");
  };

  const run = (row: Row) => {
    if (row.kind === "action") {
      closeAll();
      row.onSelect();
    } else if (row.kind === "submenu") {
      openSubmenu();
    }
  };

  const step = (ids: string[], current: string, delta: number): string | undefined => {
    if (ids.length === 0) return undefined;
    const index = ids.indexOf(current);
    if (index < 0) return delta > 0 ? ids[0] : ids[ids.length - 1];
    return ids[(index + delta + ids.length) % ids.length];
  };

  const moveTopLevel = (delta: number) => {
    const next = step(selectableIds, activeId, delta);
    if (next !== undefined) setActiveId(next);
  };

  const moveSubmenu = (delta: number) => {
    const next = step(submenuIds, submenuId, delta);
    if (next !== undefined) setSubmenuId(next);
  };

  const chooseOrg = (candidateId: string) => {
    closeAll();
    onSelectOrg(candidateId);
  };

  const rowById = (id: string): Row | undefined => rows.find((row) => row.kind !== "separator" && row.id === id);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (submenuOpen) closeSubmenu();
      else dismissToTrigger();
      return;
    }
    // Tab and Shift+Tab leave the menu at any depth the way Escape does, rather
    // than falling through to whatever sits behind a card that is still open.
    if (event.key === "Tab") {
      dismissToTrigger();
      return;
    }
    if (submenuOpen) {
      if (event.key === "ArrowDown") { event.preventDefault(); moveSubmenu(1); }
      else if (event.key === "ArrowUp") { event.preventDefault(); moveSubmenu(-1); }
      else if (event.key === "ArrowLeft") { event.preventDefault(); closeSubmenu(); }
      else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (submenuId === BACK_ROW_ID) closeSubmenu();
        else if (orgs.some((candidate) => candidate.id === submenuId)) chooseOrg(submenuId);
      }
      return;
    }
    if (event.key === "ArrowDown") { event.preventDefault(); moveTopLevel(1); }
    else if (event.key === "ArrowUp") { event.preventDefault(); moveTopLevel(-1); }
    else if (event.key === "ArrowRight") {
      if (rowById(activeId)?.kind === "submenu") { event.preventDefault(); openSubmenu(); }
    } else if (event.key === "Enter" || event.key === " ") {
      const row = rowById(activeId);
      if (row) { event.preventDefault(); run(row); }
    }
  };

  const workspaceRows = (
    <>
      {narrow ? (
        <button
          type="button"
          role="menuitem"
          id={domId(BACK_ROW_ID)}
          tabIndex={-1}
          className="account-menu-item account-menu-back"
          data-active={submenuId === BACK_ROW_ID}
          onMouseEnter={() => setSubmenuId(BACK_ROW_ID)}
          onClick={closeSubmenu}
        >
          <span aria-hidden="true">&lsaquo;</span>
          <span className="account-menu-label">Switch workspace</span>
        </button>
      ) : null}
      {userEmail ? <div className="account-menu-eyebrow truncate" role="presentation" title={userEmail}>{userEmail}</div> : null}
      {orgs.map((candidate) => (
        // A real link, so ⌘-click, middle-click and the context menu open the
        // workspace in another tab: each tab is its own workspace, the web's
        // counterpart of the desktop's one window per organization.
        <a
          key={candidate.id}
          id={domId(candidate.id)}
          href={`/o/${candidate.id}`}
          role="menuitemradio"
          aria-checked={candidate.id === orgId}
          tabIndex={-1}
          className="account-menu-item account-menu-org"
          data-active={candidate.id === submenuId}
          onMouseEnter={() => setSubmenuId(candidate.id)}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) { closeAll(); return; }
            event.preventDefault();
            chooseOrg(candidate.id);
          }}
        >
          <OrgIdentity image={candidate.image} name={candidate.name} size={16} />
          <span className="account-menu-label truncate">{candidate.name}</span>
          {candidate.id === orgId ? <Icon name="check" className="account-menu-check" /> : null}
        </a>
      ))}
    </>
  );

  const submenuDescendant = submenuIds.includes(submenuId) ? domId(submenuId) : undefined;
  const activeDescendant = submenuOpen
    ? submenuDescendant
    : selectableIds.includes(activeId) ? domId(activeId) : undefined;

  return (
    <div
      ref={menuRef}
      className="account-menu"
      role="menu"
      aria-label="Account"
      aria-activedescendant={activeDescendant}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      {narrow && submenuOpen ? (
        workspaceRows
      ) : (
        rows.map((row, index) => {
          if (row.kind === "separator") return <span key={`separator-${index}`} className="account-menu-separator" role="separator" />;
          if (row.kind === "submenu") {
            return (
              <div
                key={row.id}
                className="account-menu-submenu"
                onMouseEnter={() => {
                  setActiveId(row.id);
                  clearTimeout(hoverTimer.current);
                  hoverTimer.current = setTimeout(openSubmenu, HOVER_OPEN_DELAY_MS);
                }}
                onMouseLeave={() => clearTimeout(hoverTimer.current)}
              >
                <button
                  ref={submenuAnchorRef}
                  id={domId(row.id)}
                  type="button"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={submenuOpen}
                  tabIndex={-1}
                  className="account-menu-item"
                  data-active={row.id === activeId}
                  onClick={() => (submenuOpen ? closeSubmenu() : openSubmenu())}
                >
                  <span className="account-menu-label">{row.label}</span>
                  <Icon name="chevron" className="account-menu-chevron" />
                </button>
                {submenuOpen ? (
                  <div
                    ref={flyoutRef}
                    className={`account-menu account-menu-flyout account-menu-flyout-${placement.side}`}
                    role="menu"
                    aria-label="Switch workspace"
                    aria-activedescendant={submenuDescendant}
                    style={{ width: FLYOUT_WIDTH, marginTop: placement.shift }}
                  >
                    {workspaceRows}
                  </div>
                ) : null}
              </div>
            );
          }
          return (
            <button
              key={row.id}
              id={domId(row.id)}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`account-menu-item${row.danger ? " danger" : ""}`}
              data-active={row.id === activeId}
              onMouseEnter={() => { setActiveId(row.id); clearTimeout(hoverTimer.current); setSubmenuOpen(false); }}
              onClick={() => run(row)}
            >
              <span className="account-menu-label">{row.label}</span>
            </button>
          );
        })
      )}
    </div>
  );
}
